import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Search, Ticket as TicketIcon } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { IssueDetailModal } from "@/features/tickets/IssueDetailModal";

export const Route = createFileRoute("/_app/tickets")({
  component: TicketsPage,
});

interface AggTicket {
  id: string;
  identifier: string | null;
  sequenceId: number | null;
  title: string;
  state: string;
  stateGroup: string;
  priority: string;
  dueDate: string | null;
  updatedAt?: string;
  projectId: string;
  projectName: string;
  projectSlug: string;
  projectAvatarR2Key: string | null;
}

const STATE_LABEL: Record<string, string> = {
  backlog: "Backlog",
  unstarted: "Por iniciar",
  started: "En curso",
  completed: "Completado",
  cancelled: "Cancelado",
};
const STATE_BADGE: Record<string, string> = {
  backlog: "badge-ghost",
  unstarted: "badge-info",
  started: "badge-primary",
  completed: "badge-success",
  cancelled: "badge-warning",
};
const PRIORITY_LABEL: Record<string, string> = {
  urgent: "Urgente",
  high: "Alta",
  medium: "Media",
  low: "Baja",
  none: "—",
};
const PRIORITY_BADGE: Record<string, string> = {
  urgent: "badge-error",
  high: "badge-warning",
  medium: "badge-info",
  low: "badge-ghost",
  none: "badge-ghost",
};

function TicketsPage() {
  const { data, isLoading } = useQuery<{ tickets: AggTicket[] }>({
    queryKey: ["tickets", "aggregate"],
    queryFn: () => api.get("/tickets"),
  });

  const [projectFilter, setProjectFilter] = useState<string>("");
  const [stateFilter, setStateFilter] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [openTicket, setOpenTicket] = useState<{ projectId: string; issueId: string } | null>(null);

  const tickets = data?.tickets ?? [];

  const projects = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const t of tickets) map.set(t.projectId, { id: t.projectId, name: t.projectName });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [tickets]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tickets.filter((t) => {
      if (projectFilter && t.projectId !== projectFilter) return false;
      if (stateFilter && t.stateGroup !== stateFilter) return false;
      if (q) {
        const hay = `${t.title} ${t.identifier ?? ""} ${t.projectName}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tickets, projectFilter, stateFilter, search]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {
      backlog: 0,
      unstarted: 0,
      started: 0,
      completed: 0,
      cancelled: 0,
    };
    for (const t of tickets) {
      if (projectFilter && t.projectId !== projectFilter) continue;
      c[t.stateGroup] = (c[t.stateGroup] ?? 0) + 1;
    }
    return c;
  }, [tickets, projectFilter]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Tickets"
        title="Todos los tickets"
        subtitle="Lo que el equipo está trabajando en tus proyectos. Click en un ticket abre su detalle."
      />

      <div className="card-elevated p-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
          <input
            type="text"
            placeholder="Buscar por título o identifier…"
            className="input input-bordered input-sm w-full pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="select select-bordered select-sm"
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value)}
        >
          <option value="">Todos los proyectos</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <select
          className="select select-bordered select-sm"
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
        >
          <option value="">Todos los estados</option>
          {["backlog", "unstarted", "started", "completed", "cancelled"].map((s) => (
            <option key={s} value={s}>
              {STATE_LABEL[s]} ({counts[s] ?? 0})
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="grid place-items-center py-16">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="card-elevated p-10 text-center text-sm text-base-content/55">
          {tickets.length === 0
            ? "Aún no hay tickets visibles. Si crees que es un error, contacta a tu lead en Axorial."
            : "Ningún ticket coincide con los filtros."}
        </div>
      ) : (
        <section className="card-elevated overflow-hidden">
          <ul className="divide-y divide-base-300/40">
            {filtered.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => setOpenTicket({ projectId: t.projectId, issueId: t.id })}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-base-200/40 transition-colors text-left"
                >
                  {t.projectAvatarR2Key ? (
                    <img
                      src={`/api/projects/${t.projectId}/avatar`}
                      alt=""
                      className="size-8 rounded object-cover shrink-0"
                    />
                  ) : (
                    <div className="size-8 rounded bg-primary/15 grid place-items-center shrink-0">
                      <TicketIcon className="size-4 text-primary" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    {t.identifier && (
                      <div className="text-[10px] font-mono text-base-content/55">
                        {t.identifier}
                      </div>
                    )}
                    <div className="font-medium text-sm leading-snug line-clamp-2">{t.title}</div>
                    <div className="text-xs text-base-content/55 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span>{t.projectName}</span>
                      {t.dueDate && <span>· vence {t.dueDate}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className={`badge badge-xs ${STATE_BADGE[t.stateGroup] ?? "badge-ghost"}`}>
                      {STATE_LABEL[t.stateGroup] ?? t.state}
                    </span>
                    {t.priority !== "none" && (
                      <span className={`badge badge-xs ${PRIORITY_BADGE[t.priority] ?? "badge-ghost"}`}>
                        {PRIORITY_LABEL[t.priority] ?? t.priority}
                      </span>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {openTicket && (
        <IssueDetailModal
          projectId={openTicket.projectId}
          issueId={openTicket.issueId}
          onClose={() => setOpenTicket(null)}
        />
      )}
    </div>
  );
}
